import {Locale} from '../constants'
import {currencies, Currency} from '../currency'
import {getLanguage} from '../i18n/language'
import {parseToLocaleAndCleanedPath} from '../i18n/restrictedPaths'
import {getPreferredPathLocale} from '../i18n/routing'
import {saveLanguageChoice} from '../preferences/choices'
import {routeTo} from '../router'

// syncs profile currency/language after login
export const syncUserProfileSettings = async (
  language: Locale, // current locale
  currency: Currency, // current currency
  setCurrency: (currency: Currency) => void,
): Promise<{language: Locale; currency: Currency} | null> => {
  const userResponse = await fetch(routeTo.api.user.me())
  if (userResponse.ok) {
    const user = await userResponse.json()
    const profileCurrency = user.profile.currency
    const profileLanguage = user.profile.language

    if (profileCurrency && profileCurrency in currencies && profileCurrency !== currency) {
      setCurrency(profileCurrency as Currency)
    }

    if (profileLanguage) {
      const resolvedProfileLanguage = getLanguage(profileLanguage)
      saveLanguageChoice(resolvedProfileLanguage)
      return {language: resolvedProfileLanguage, currency: profileCurrency}
    }

    return {language, currency: profileCurrency}
  }

  return null
}

/*
 * Signing in switches to the profile language, as if the visitor had picked it - but only to the
 * version of the page they continue on, never off to another page. Only that page knows its
 * versions (its hreflang links), and signing in may finish elsewhere: a magic link signs in on its
 * own page, then returns to the one it was requested from. So signing in leaves a one-time mark
 * naming the page it continues on, and that page switches once the profile has loaded
 * (`LanguageSuggestion`). The homepage and the portal have the same path in every language, so a
 * sign-in continuing there opens in the profile language directly, where that language serves
 * them (`getSignInLocale`). See "Switching language" in docs/visitor-preferences.md.
 */

export const getSignInLocale = (url: string, profileLanguage: Locale | undefined) => {
  const {locale, cleanedPath} = parseToLocaleAndCleanedPath(
    new URL(url, window.location.origin).pathname,
  )
  return getPreferredPathLocale(cleanedPath, profileLanguage) ?? getLanguage(locale)
}

const signInLanguageSwitchKey = 'signInLanguageSwitch'

const getPathKey = (url: string) =>
  parseToLocaleAndCleanedPath(new URL(url, window.location.origin).pathname).cleanedPath

export const markSignInLanguageSwitch = (url: string) => {
  try {
    sessionStorage.setItem(signInLanguageSwitchKey, getPathKey(url))
  } catch {
    // storage unavailable (private browsing): the page stays in its language
  }
}

// Whether this page is the one a sign-in marked, clearing the mark
export const takeSignInLanguageSwitch = () => {
  try {
    if (sessionStorage.getItem(signInLanguageSwitchKey) !== getPathKey(window.location.href)) {
      return false
    }
    sessionStorage.removeItem(signInLanguageSwitchKey)
    return true
  } catch {
    return false
  }
}
